/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2026, Bulb Inc. For more information, please email contact@hellobulb.com.
 */

/* eslint-disable import/no-extraneous-dependencies */
import {
  ButtonView, IconLink, LinkUI, Plugin, type ModelDocumentSelection,
} from 'ckeditor5';
/* eslint-enable import/no-extraneous-dependencies */
import BulbImageEditing from '../bulbimageediting';

/** Registers the image-link button using CKEditor's standard link balloon. */
export default class BulbLinkImageUI extends Plugin {

  public static get pluginName(): string {
    return 'BulbLinkImageUI' as const;
  }

  public static get requires() {
    return [BulbImageEditing, LinkUI] as const;
  }

  public init(): void {
    const { editor } = this;

    this.listenTo(editor.editing.view.document, 'click', (event, data) => {
      if (BulbLinkImageUI.isSelectedLinkedImage(editor.model.document.selection)) {
        data.preventDefault();
        event.stop();
      }
    }, { priority: 'high' });

    editor.ui.componentFactory.add('linkImage', (locale) => {
      const button = new ButtonView(locale);
      const linkCommand = editor.commands.get('link');
      const linkUI = editor.plugins.get(LinkUI);

      button.set({
        label: editor.t('Link image'),
        icon: IconLink,
        tooltip: true,
        isToggleable: true,
      });

      button.bind('isEnabled').to(linkCommand, 'isEnabled');
      button.bind('isOn').to(linkCommand, 'value', (value) => !!value);

      this.listenTo(button, 'execute', () => {
        if (BulbLinkImageUI.isSelectedLinkedImage(editor.model.document.selection)) {
          // CKEditor's own LinkImageUI uses these internal methods to share the standard link balloon.
          // eslint-disable-next-line no-underscore-dangle
          linkUI._addToolbarView();
        } else {
          // eslint-disable-next-line no-underscore-dangle
          linkUI._showUI(true);
        }
      });

      return button;
    });
  }

  private static isSelectedLinkedImage(selection: ModelDocumentSelection): boolean {
    const selectedElement = selection.getSelectedElement();
    return !!selectedElement
      && selectedElement.is('element', 'image')
      && selectedElement.hasAttribute('linkHref');
  }
}
